import { useEffect, useRef, type CSSProperties } from 'react';
import { lookFrames, type LookFrames, type LookKind } from '../render/lookFrames.js';
import type { OutfitColors } from '../render/outfitIcon.js';

/**
 * Walking outfit / mount preview — the reference renderer (chunk
 * "outfit-renderer"): a canvas redrawn whole on each frame, frames picked by
 * `floor(((now − T0) % W) / W × n)` with a 540 ms cycle, the next redraw
 * scheduled with setTimeout exactly at the next frame boundary (no rAF, no
 * React state per frame). Drawn like the reference "world" mode: scale
 * min(w/bounds.w, h/bounds.h, min(w,h)/64), floored to a whole factor when
 * above 1, centred on the union of the frames' opaque pixels with Math.floor,
 * imageSmoothingEnabled = false and CSS image-rendering: pixelated.
 */

const CYCLE_MS = 540;

export function drawLookFrame(canvas: HTMLCanvasElement, frames: LookFrames, index: number): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const frame = frames.frames[index] ?? frames.frames[0];
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  if (!frame) return;
  const box = frames.bounds ?? { x: 0, y: 0, w: frame.width, h: frame.height };
  let scale = Math.min(canvas.width / box.w, canvas.height / box.h, Math.min(canvas.width, canvas.height) / 64);
  if (scale > 1) scale = Math.floor(scale);
  const w = Math.round(box.w * scale);
  const h = Math.round(box.h * scale);
  ctx.drawImage(frame, box.x, box.y, box.w, box.h, Math.floor((canvas.width - w) / 2), Math.floor((canvas.height - h) / 2), w, h);
}

export function OutfitWalker({
  kind = 'outfits',
  lookType,
  colors,
  addon = 0,
  direction = 2,
  size = 192,
  speed = 1,
  atlas,
  className,
  style,
  label,
}: {
  kind?: LookKind;
  lookType: number;
  colors?: OutfitColors | null;
  addon?: number;
  direction?: number;
  /** Canvas pixels (CSS size follows unless overridden). */
  size?: number;
  speed?: number;
  /** Small atlas read first (lookFrames option `atlas`), e.g. the about page's 'about-outfits'. */
  atlas?: string;
  className?: string;
  style?: CSSProperties;
  label?: string;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const colorKey = colors ? `${colors.head}-${colors.body}-${colors.legs}-${colors.feet}` : '';
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !lookType) return undefined;
    let timer: number | null = null;
    let live = true;
    let start: number | null = null;
    void lookFrames(kind, lookType, { walking: true, direction, colors: colors ?? null, addon, walkPhaseMs: CYCLE_MS / 8, atlas }).then((frames) => {
      if (!live || !frames || !frames.frames.length) return;
      const cycle = CYCLE_MS / Math.max(0.01, speed);
      start ??= performance.now();
      const tick = () => {
        timer = null;
        if (!live || !canvas.isConnected) return;
        const elapsed = performance.now() - start!;
        const index = Math.floor(((elapsed % cycle) / cycle) * frames.frames.length);
        drawLookFrame(canvas, frames, index);
        if (frames.frames.length > 1) {
          const step = cycle / frames.frames.length;
          timer = window.setTimeout(tick, Math.max(1, step - (elapsed % step)));
        }
      };
      tick();
    });
    return () => {
      live = false;
      if (timer !== null) window.clearTimeout(timer);
    };
    // colors are compared by value (colorKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, lookType, colorKey, addon, direction, speed, size, atlas]);
  return (
    <canvas
      ref={ref}
      className={className}
      width={size}
      height={size}
      style={{ width: size, height: size, imageRendering: 'pixelated', ...style }}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    />
  );
}
